import {loadEnv} from '../../util/loadEnv.js'

const appEnvPrefix = 'SANITY_APP_'
const studioEnvPrefix = 'SANITY_STUDIO_'

/**
 * The params for the `getStudioEnvironmentVariables` and `getAppEnvironmentVariables` function
 * that gets Studio/App-focused environment variables.
 *
 * @public
 */
export interface StudioEnvVariablesOptions {
  /**
   * When specified includes environment variables from dotenv files (`.env`), in the same way the studio does.
   * A `mode` must be specified, usually `development`
   * or `production`, which will load the corresponding `.env.development` or `.env.production`.
   * To specify where to look for the dotenv files, specify `options.envFile.envDir`.
   */
  envFile?: false | {envDir?: string; mode: string}
  /**
   * When specified, JSON-encodes the values, which is handy if you want to pass
   * this to a bundlers hardcoded defines, such as Vite's `define` or Webpack's `DefinePlugin`.
   */
  jsonEncode?: boolean
  /**
   * When specified adds a prefix to the environment variable keys,
   * eg: `getStudioEnvironmentVariables({prefix: 'process.env.'})`
   */
  prefix?: string
}

/**
 * Get environment variables prefixed with SANITY_STUDIO_, as an object.
 *
 * @param options - Options for the environment variable loading
 *  {@link StudioEnvVariablesOptions}
 * @returns Object of studio environment variables
 *
 * @example
 * ```tsx
 * getStudioEnvironmentVariables({prefix: 'process.env.', jsonEncode: true})
 * ```
 *
 * @public
 */
export function getStudioEnvironmentVariables(
  options: StudioEnvVariablesOptions = {},
): Record<string, string> {
  return getEnvironmentVariables({...options, varTypePrefix: studioEnvPrefix})
}

/**
 * Get environment variables prefixed with SANITY_APP_, as an object.
 *
 * @param options - Options for the environment variable loading
 *  {@link StudioEnvVariablesOptions}
 * @returns Object of app environment variables
 *
 * @internal
 */
export function getAppEnvironmentVariables(
  options: StudioEnvVariablesOptions = {},
): Record<string, string> {
  return getEnvironmentVariables({...options, varTypePrefix: appEnvPrefix})
}

function getEnvironmentVariables(
  options: StudioEnvVariablesOptions & {varTypePrefix: string},
): Record<string, string> {
  const {envFile = false, jsonEncode = false, prefix = '', varTypePrefix} = options
  const fullEnv = envFile
    ? {...process.env, ...loadEnv(envFile.mode, envFile.envDir || process.cwd(), [varTypePrefix])}
    : process.env

  const appEnv: Record<string, string> = {}
  for (const key in fullEnv) {
    if (key.startsWith(varTypePrefix)) {
      appEnv[`${prefix}${key}`] = jsonEncode
        ? JSON.stringify(fullEnv[key] || '')
        : fullEnv[key] || ''
    }
  }
  return appEnv
}
